import { ReactNode, useMemo, useState } from 'react';

import ImageRemote from 'inquiry/components/ImageRemote';
import type { ImageRemoteComponent } from 'lib/interfaces/ui-components';

interface CustomAssetWithFallbackProps {
  customImage?: ImageRemoteComponent | null;
  children?: ReactNode;
  onLoad?: () => void;
  environment?: string;
  /**
   * While custom images can be configured to have alt text at the template
   * level, we still enforce a hardcoded alt text as a fallback to ensure there
   * is always accessible text.
   *
   * This is used as both alt text and aria-label. alt text does not benefit
   * screen readers.
   */
  alt: string;
}

const CustomAssetWithFallback = ({
  customImage,
  children = null,
  onLoad,
  environment,
  alt,
}: CustomAssetWithFallbackProps) => {
  const [isErrored, setIsErrored] = useState(false);

  /**
   * Merges in alt text from parent. Still allows overriding via component config.
   */
  const customImageWithAltText: ImageRemoteComponent | undefined = useMemo(() => {
    if (!customImage) return;

    if (!alt) {
      return customImage;
    } else {
      return {
        ...customImage,
        attributes: {
          ...customImage.attributes,
          alt: customImage.attributes.alt ?? alt,
        },
      };
    }
  }, [customImage, alt]);

  if (customImageWithAltText && !isErrored) {
    return (
      <ImageRemote
        component={customImageWithAltText}
        onLoad={onLoad}
        onError={() => setIsErrored(true)}
        environment={environment}
      />
    );
  } else {
    onLoad?.();
    return <>{children}</>;
  }
};

export default CustomAssetWithFallback;
